<script>
  import { createEventDispatcher } from "svelte";
  import Popup from "./Popup.svelte";
  import { tourFunctions } from "../tour-functions";

  export let tour;
  export let visible = true;

  const dispatch = createEventDispatcher();

  $: heading = tour?.mapIntro?.heading ?? "";
  $: logoUrl = tour?.mapIntro?.logoUrl ?? "";
  $: logoWidth = tour?.mapIntro?.logoWidth ?? null;
  $: logoHeight = tour?.mapIntro?.logoHeight ?? null;
  $: contentHtml = tour?.mapIntro?.contentHtml ?? "";
  $: videoEmbedUrl = tour?.mapIntro?.videoEmbedUrl ?? "";
  $: buttonLabel = (tour?.mapIntro?.buttonLabel || "").trim() || "Start";

  const parseContent =
    tourFunctions.parseContent ||
    ((s) => (s || "").replaceAll("[", "<").replaceAll("]", ">"));

  function handleStart() {
    dispatch("start");
    dispatch("close");
  }
</script>

<Popup
  {visible}
  ariaLabel="Map intro"
  width={tour?.mapIntro?.width || "80%"}
  on:close={() => dispatch("close")}
>
  <div class="map-intro-popup">
    <div class="map-intro-main">
      {#if logoUrl}
        <div class="map-intro-logo-wrap">
          <img
            src={logoUrl}
            alt={heading ? `${heading} logo` : "Map intro logo"}
            class="map-intro-logo"
            style={`width: ${logoWidth ? `${logoWidth}px` : "auto"}; height: ${logoHeight ? `${logoHeight}px` : "auto"};`}
            loading="lazy"
          />
        </div>
      {/if}
      {#if heading}
        <h2 class="map-intro-heading">{heading}</h2>
      {/if}
      {#if contentHtml}
        <div class="map-intro-content" class:has-video={videoEmbedUrl}>
          {@html parseContent(contentHtml)}
        </div>
      {/if}

      {#if videoEmbedUrl}
        <div class="map-intro-video">
          <div class="video-wrapper">
            <iframe
              src={videoEmbedUrl}
              title="Map intro video"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
            ></iframe>
          </div>
        </div>
      {/if}
    </div>

    <div class="map-intro-actions">
      <button type="button" class="start-map-button" on:click={handleStart}>
        {buttonLabel}
      </button>
    </div>
  </div>
</Popup>

<style>
  .map-intro-popup {
    padding-top: 2rem;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    max-width: 100%;
  }

  .map-intro-main {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .map-intro-heading {
    margin: 0;
    padding: 0;
    font-size: 1.5rem;
    font-weight: 700;
    font-family: var(--font-heading), sans-serif;
    position: sticky;
    top: 0;
    z-index: 3;
    background: var(--popup-bg-color);
  }

  .map-intro-logo-wrap {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    width: 100%;
    margin-bottom: 1rem;
  }

  .map-intro-logo {
    max-width: 100%;
    object-fit: contain;
    display: block;
  }

  .map-intro-content {
    line-height: 1.6;
    margin-bottom: 1.5rem;
  }

  .map-intro-content :global(p) {
    margin-bottom: 0.75rem;
  }

  .map-intro-content :global(p:last-child) {
    margin-bottom: 0;
  }

  .map-intro-video {
    width: 100%;
  }

  .video-wrapper {
    position: relative;
    width: 100%;
    padding-bottom: 56.25%;
    height: 0;
    overflow: hidden;
    border-radius: 0.85rem;
  }

  .video-wrapper iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .map-intro-actions {
    margin-top: 0.5rem;
    display: flex;
    justify-content: center;
    width: 100%;
  }

  .start-map-button {
    appearance: none;
    padding: 0.75rem 1.5rem;
    font-size: 1.25rem;
    font-weight: 700;
    font-family: var(--font-heading), sans-serif;
    color: var(--popup-text-color);
    background: rgba(var(--theme-color-rgb), 0.9);
    border: 2px solid var(--popup-text-color);
    border-radius: 4px;
    cursor: pointer;
  }

  .start-map-button:hover {
    background: rgba(var(--theme-color-rgb), 1);
  }

  .start-map-button:focus-visible {
    outline: 4px solid var(--focus-color);
    outline-offset: 2px;
  }
</style>
